import { NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { EmailRequestDto } from '@core/interfaces/email.interface';
import { AuthService } from '@core/services/auth.service';
import { EmailService } from '@core/services/email.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { FormUtils } from '@core/utils/form-utils';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { IKeyValue } from '@shared/interfaces/key-value.interface';
import { LanguageService } from '@shared/services/language.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { ReCaptchaV3Service, RecaptchaV3Module } from 'ng-recaptcha';
import { Subscription } from 'rxjs';
import { EntityService } from 'src/app/entities-manager/services/entity.service';
import { EmailSentDialogComponent } from '../email-sent-dialog/email-sent-dialog.component';

@Component({
  selector: 'app-contacts-dialog',
  templateUrl: './contacts-dialog.component.html',
  styleUrls: ['./contacts-dialog.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    FontAwesomeModule,
    NgIf,
    NgFor,
    TranslatePipe,
    RecaptchaV3Module,
  ],
})
@UntilDestroy()
export class ContactsDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  faArrowRight = faArrowRight;
  faArrowLeft = faArrowLeft;

  @Input() isMemorabid: boolean = true;
  @Input() inPreEventStatus: boolean = true;
  @Input() onClickIconMessageHeader: string;
  @Input() idAuction: number = -1;

  private subscription: Subscription;
  acceptPolicy = true;
  policyNotAccepted = false;

  emailSubjects: IKeyValue[];

  subjectBecomeSeller: string = '/' + this.router.url.split('/').at(-1);

  subject: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isEmailInvalid = false;
  emailSent = false;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    public authService: AuthService,
    private entityService: EntityService,
    private publicGenericService: PublicGenericService,
    private emailService: EmailService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private modalUtils: ModalUtilsService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    if (this.authService.userLogged?.idEntity != -1) {
      this.entityService
        .getByPk(this.authService.userLogged?.idEntity)
        .pipe(untilDestroyed(this))
        .subscribe((dataR) => {
          this.firstName = dataR.firstName;
          this.lastName = dataR.lastName;
          this.email = dataR.emailAddress;
          this.phone = dataR.phone;
        });
    }

    if (this.onClickIconMessageHeader != null) {
      this.subjectBecomeSeller = this.onClickIconMessageHeader;
    }

    /* set default subject */
    if (this.subjectBecomeSeller == '/become-seller') {
      this.subject = 'IDS_WANT_BECOME_SELLER_SUBJECT';
    } else if (this.subjectBecomeSeller == '/about-us') {
      this.subject = 'IDS_CONTACT_ABOUT_US_GENERIC_REQUEST';
    } else {
      this.publicGenericService
        .GetAllEmailSubjects()
        .pipe(untilDestroyed(this))
        .subscribe((dataR) => {
          this.emailSubjects = dataR;
          if (dataR.length > 0) {
            this.subject = dataR[0].ids;
          }
        });
    }
  }

  ngAfterViewInit() {
    this.emailService.showReCaptchaBadge();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.emailService.hideReCaptchaBadge();
  }

  contact(contactForm: NgForm) {
    if (!this.acceptPolicy) {
      this.policyNotAccepted = true;
    } else {
      this.policyNotAccepted = false;

      const userLanguageCode = this.languageService.userSelectedLanguage
        ? this.languageService.userSelectedLanguage.code
        : 'it';

      const formData = contactForm.value;
      const emailRequest: EmailRequestDto = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        subject: formData.subject,
        message:
          formData.moreinfo == null
            ? formData.message
            : formData.message + '<br>' + formData.moreinfo,
        idAuction: this.idAuction,
        languageCode: userLanguageCode,
      };

      if (contactForm.invalid) {
        for (const control of Object.keys(contactForm.controls)) {
          contactForm.controls[control].markAsTouched();
        }
        return;
      }

      if (FormUtils.emailRegex.test(emailRequest.email)) {
        this.isEmailInvalid = false;
        this.subscription = this.recaptchaV3Service
          .execute('contactForm')
          .subscribe((token: string) => {
            this.emailService
              .verifyReCaptcha({ responseToken: token })
              .pipe(untilDestroyed(this))
              .subscribe(() => {
                this.emailService
                  .sendContactFormEmail(emailRequest)
                  .pipe(untilDestroyed(this))
                  .subscribe(() => {
                    this.emailSent = true;
                    const ref = this.modalUtils.openModal(
                      EmailSentDialogComponent,
                      { size: 'md', keyboard: false }
                    );
                    ref.result.then((_result) => {
                      this.activeModal.close();
                    });
                  });
              });
          });
      } else {
        this.isEmailInvalid = true;
      }
    }
  }

  goToPage(route: string) {
    this.activeModal.close();
    this.router.navigate([route]);
  }
}
